import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Program, ProgramItem } from '@app/app/page-modules/programs/models/program.model';
import { LanguageCodeHelper } from '@app/app/shared/helpers/language-code-helper';
import { Page } from '@app/app/shared/models/page';
import { RestClientService } from '@app/app/shared/services/rest-client.service';
import { ObservableResult } from '@app/app/shared/store';
import { environment } from '@app/environments/environment';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, switchMap } from 'rxjs/operators';
import {
  CreateProgramFromTemplateRequest,
  CreateProgramResponse,
  ProgramCreationRequest,
  ProgramDataService,
  ProgramSectionBulkUpdateItem,
  ProgramSectionRequestUnion,
  ProgramSectionResponse,
  ProgramUpdateRequest,
  UpdateProgramResponse,
} from './data.service';

@Injectable()
export class ApiProgramDataService implements ProgramDataService {
  constructor(
    private client: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  getPrograms(orgClientId?: string): ObservableResult<Page<ProgramItem>> {
    const params: Record<string, string> = {};
    if (orgClientId) {
      params['orgClientId'] = orgClientId;
    }
    return this.client.get<Page<ProgramItem>>(Location.joinWithSlash(environment.apiRootUrl || '', 'programs'), params).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAllPrograms'))),
    );
  }

  getProgram(programUri: string, languageCode?: string, playlistPath?: string): ObservableResult<Program> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `programs/${programUri}`);
    const params = playlistPath ? { in: playlistPath } : null;

    return this.client.get<Program>(url, params, languageCode ? { 'Accept-Language': languageCode } : null).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetProgramDetail'))),
    );
  }

  createProgram(request: ProgramCreationRequest): ObservableResult<CreateProgramResponse> {
    const requestBody = { ...request, type: 'PROGRAM' };

    return this.client
      .post<CreateProgramResponse>(
        Location.joinWithSlash(environment.apiRootUrl || '', 'playlists'),
        requestBody,
        undefined,
        request.languageCode ? LanguageCodeHelper.checkAndGetContentLanguageCode(request.languageCode) : null,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError((err) => ObservableResult.ofError(err)),
      );
  }

  createProgramFromTemplate(request: CreateProgramFromTemplateRequest): ObservableResult<CreateProgramResponse> {
    const queryParams: { [key: string]: string } = {};
    if (request.in) {
      queryParams['in'] = request.in;
    }
    if (request.section) {
      queryParams['section'] = request.section;
    }

    const url = Location.joinWithSlash(environment.apiRootUrl || '', `playlists/templates/${request.templateUid}`);
    const headers = request.languageCode ? LanguageCodeHelper.checkAndGetContentLanguageCode(request.languageCode) : null;

    return this.client.post<CreateProgramResponse>(url, {}, Object.keys(queryParams).length > 0 ? queryParams : undefined, headers).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError((err) => ObservableResult.ofError(err)),
    );
  }

  updateProgram(programUid: string, request: ProgramUpdateRequest, languageCode?: string): ObservableResult<UpdateProgramResponse> {
    return this.client
      .patch<UpdateProgramResponse>(
        Location.joinWithSlash(environment.apiRootUrl || '', `playlists/${programUid}`),
        request,
        {},
        languageCode ? LanguageCodeHelper.checkAndGetContentLanguageCode(languageCode) : null,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateProgram'))),
      );
  }

  addProgramSection(
    programUid: string,
    request: ProgramSectionRequestUnion,
    languageCode?: string,
  ): ObservableResult<ProgramSectionResponse> {
    return this.client
      .put<ProgramSectionResponse>(
        Location.joinWithSlash(environment.apiRootUrl || '', `programs/${programUid}/sections`),
        request,
        {},
        languageCode ? LanguageCodeHelper.checkAndGetContentLanguageCode(languageCode) : null,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body || {})),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorCreateSection'))),
      );
  }

  updateProgramSection(
    programUid: string,
    sectionUid: string,
    request: ProgramSectionRequestUnion,
    languageCode?: string,
  ): ObservableResult<ProgramSectionResponse> {
    return this.client
      .post<ProgramSectionResponse>(
        Location.joinWithSlash(environment.apiRootUrl || '', `programs/${programUid}/sections/${sectionUid}`),
        request,
        {},
        languageCode ? LanguageCodeHelper.checkAndGetContentLanguageCode(languageCode) : null,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body || {})),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateSection'))),
      );
  }

  bulkUpdateProgramSections(programUid: string, sections: ProgramSectionBulkUpdateItem[]): ObservableResult<void> {
    const requestBody = sections.map((item) => ({
      uid: item.uid,
      ...item.request,
    }));

    return this.client
      .post<void>(Location.joinWithSlash(environment.apiRootUrl || '', `programs/${programUid}/sections`), requestBody)
      .pipe(
        switchMap(() => ObservableResult.ofSuccess()),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateSections'))),
      );
  }

  deleteProgramSection(programUid: string, sectionUid: string): ObservableResult<void> {
    return this.client
      .delete<void>(Location.joinWithSlash(environment.apiRootUrl || '', `programs/${programUid}/sections/${sectionUid}`))
      .pipe(
        switchMap(() => ObservableResult.ofSuccess()),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorRemovingSection'))),
      );
  }

  reorderProgramSections(programUid: string, request: { sections: string[] }): ObservableResult<void> {
    return this.client
      .put<void>(Location.joinWithSlash(environment.apiRootUrl || '', `programs/${programUid}/sections/reorder`), request)
      .pipe(
        switchMap(() => ObservableResult.ofSuccess()),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorReorderSections'))),
      );
  }

  publishProgram(programUid: string): ObservableResult<void> {
    return this.client.post<void>(Location.joinWithSlash(environment.apiRootUrl || '', `playlists/${programUid}/publication`)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorPublishProgram'))),
    );
  }

  unpublishProgram(programUid: string): ObservableResult<void> {
    return this.client.delete<void>(Location.joinWithSlash(environment.apiRootUrl || '', `playlists/${programUid}/publication`)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUnpublishProgram'))),
    );
  }

  archiveProgram(programUid: string): ObservableResult<void> {
    return this.client.post<void>(Location.joinWithSlash(environment.apiRootUrl || '', `playlists/${programUid}/archives`)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorArchiveProgram'))),
    );
  }
}
